import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import {
  GridDropdown,
  LabelHover,
  EnhancedSlider,
} from '../../utils/gridUtils';
import {
  setObjectPhysicsProperties,
  updatePhysicsProperties,
  applyPhysicsToObject as applyPhysicsThunk,
  removePhysicsFromObject,
} from '../../redux/Slice/physicsSlice';
import { updateActiveComponent } from '../../redux/Slice/globalStates';
import {
  applyPhysicsToObject as applyPhysicsUtil,
  updateObjectPhysics as updateObjectPhysicsUtil,
  hasPhysics as hasPhysicsUtil,
  removePhysicsFromObject as removePhysicsUtil,
} from '../../utils/Physics/PhysicsUtils';
import {
  findPhysicsDataForObject,
  getPhysicsObjectId,
} from '../../utils/Physics/physicsObjectMatch';

const getObjectDisplayName = (object) => {
  if (!object) return '';

  const displayName =
    object.file_name ||
    object.asset_file_name ||
    object.userData?.file_name;
  if (displayName) return displayName;

  const id = object.name || '';
  const tempMatch = id.match(/^temp-\d+-(.+)$/);
  if (tempMatch) return tempMatch[1];

  return id;
};

const PHYSICS_LABEL_FLEX = 'shrink-0 w-[90px]';
const PHYSICS_CONTROL_CLASS = 'w-[130px] shrink-0 flex items-center justify-start';

const PhysicsRow = ({ label, children, className = '' }) => (
  <div className={`flex items-center py-1 text-xs w-full ${className}`}>
    <LabelHover label={label} flex={PHYSICS_LABEL_FLEX} />
    <div className={PHYSICS_CONTROL_CLASS}>{children}</div>
  </div>
);

const PhysicsCheckbox = ({ checked, onToggle }) => (
  <div
    className="w-4 h-4 bg-[#2B2C2F] border border-gray-700 flex items-center justify-center cursor-pointer rounded"
    onClick={onToggle}
  >
    {checked && (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 text-white"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    )}
  </div>
);

const GridInput = ({
  label,
  value,
  onChange,
  type = 'number',
  step = '0.01',
}) => (
  <PhysicsRow label={label}>
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      step={step}
      className="bg-[#2b2c2f] text-gray-200 px-1 py-1 rounded w-full text-left text-[10px] border-none focus:outline-none"
    />
  </PhysicsRow>
);

// Small wrapper to keep EnhancedSlider usage consistent with other components
const EnhancedSliderControl = ({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
}) => (
  <PhysicsRow label={label}>
    <div className="w-full physics-slider-control [&>div]:pb-0 [&>div]:mb-0 [&>div>div>div]:pl-0 [&>div>div>div]:w-full">
      <EnhancedSlider
        value={Number(value)}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
      />
    </div>
  </PhysicsRow>
);

const Vector3Input = ({ label, x, y, z, onChangeX, onChangeY, onChangeZ }) => (
  <PhysicsRow label={label}>
    <div className="flex gap-[6px]">
      {['X', 'Y', 'Z'].map((axis, idx) => {
        const value = idx === 0 ? x : idx === 1 ? y : z;
        const onChange =
          idx === 0 ? onChangeX : idx === 1 ? onChangeY : onChangeZ;
        return (
          <div key={axis} className="flex items-center gap-1">
            <span className="text-gray-400 text-[10px]">{axis}</span>
            <input
              type="number"
              value={value || ''}
              onChange={onChange}
              step="0.01"
              className="bg-[#2b2c2f] text-gray-200 px-1 py-0.5 rounded w-[28px] text-[10px] border-none focus:outline-none"
            />
          </div>
        );
      })}
    </div>
  </PhysicsRow>
);

const CollapsibleSection = ({ title, isOpen, onToggle, children }) => (
  <div className="">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-1  text-[10px] text-gray-300 "
    >
      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      <span className="text-[10px]">{title}</span>
    </button>
    {isOpen && <div className="pb-1">{children}</div>}
  </div>
);

const buildFormDataFromPhysics = (physicsData) => ({
  mass: physicsData.mass?.toString() || '5',
  linearDamping: physicsData.linearDamping?.toString() || '0.1',
  angularDamping: physicsData.angularDamping?.toString() || '0.1',
  automaticCenterOfMass: physicsData.automaticCenterOfMass ?? true,
  centerOfMassX: (physicsData.centerOfMass?.x ?? 0).toString(),
  centerOfMassY: (physicsData.centerOfMass?.y ?? 0).toString(),
  centerOfMassZ: (physicsData.centerOfMass?.z ?? 0).toString(),
  automaticTensor: physicsData.automaticTensor ?? true,
  inertiaTensorX: physicsData.inertiaTensor?.x?.toString() || '1',
  inertiaTensorY: physicsData.inertiaTensor?.y?.toString() || '1',
  inertiaTensorZ: physicsData.inertiaTensor?.z?.toString() || '1',
  inertiaTensorRotationX:
    physicsData.inertiaTensorRotation?.x?.toString() || '0',
  inertiaTensorRotationY:
    physicsData.inertiaTensorRotation?.y?.toString() || '0',
  inertiaTensorRotationZ:
    physicsData.inertiaTensorRotation?.z?.toString() || '0',
  useGravity: physicsData.useGravity ?? true,
  isKinematic: physicsData.isKinematic ?? false,
  interpolate: physicsData.interpolate || 'None',
  collisionDetection: physicsData.collisionDetection || 'Discrete',
  freezePositionX: physicsData.freezePosition?.x ?? false,
  freezePositionY: physicsData.freezePosition?.y ?? false,
  freezePositionZ: physicsData.freezePosition?.z ?? false,
  freezeRotationX: physicsData.freezeRotation?.x ?? false,
  freezeRotationY: physicsData.freezeRotation?.y ?? false,
  freezeRotationZ: physicsData.freezeRotation?.z ?? false,
  colliderType: physicsData.colliderType || 'Box',
  isViewCollider: physicsData.isViewCollider ?? true,
  physicsMaterial: physicsData.physicsMaterial?.name || 'Default',
  friction: physicsData.physicsMaterial?.friction?.toString() || '0.5',
  restitution: physicsData.physicsMaterial?.restitution?.toString() || '0.7',
});

const PhysicsComponent = ({
  selectedObject,
  onBack,
  scene,
  historyUiRevision = 0,
}) => {
  const dispatch = useDispatch();
  const {
    physicsObjects,
    defaultPhysicsProperties,
    physicsMaterials,
    colliderTypes,
  } = useSelector((state) => state.physics);
  const { animationObjects } = useSelector((state) => state.animation);
  const { select_object } = useSelector((state) => state.assets_object);
  const { window_type } = useSelector((state) => state.window);

  // Only apply physics to world in preview mode
  const isPreviewMode = window_type === 'preview';

  // Resolve selected object and its saved physics entry
  const activeObject = select_object || selectedObject;
  const currentObjectId = getPhysicsObjectId(activeObject, physicsObjects);
  const currentPhysicsData = findPhysicsDataForObject(
    activeObject,
    physicsObjects
  );
  const initialData = currentPhysicsData || defaultPhysicsProperties;

  const [data, setData] = useState(() =>
    buildFormDataFromPhysics(initialData)
  );

  const [sectionsOpen, setSectionsOpen] = useState({
    constraints: false,
    materials: false,
    colliders: false,
  });

  // Reload form only when selection changes — not when Redux patches the same object
  // (e.g. View Collider / collider type), which was resetting collider type to Box.
  useEffect(() => {
    if (!activeObject?.name) return;

    const saved = findPhysicsDataForObject(activeObject, physicsObjects);
    setData(buildFormDataFromPhysics(saved || defaultPhysicsProperties));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeObject?.name, historyUiRevision]);

  const handleChange = (field, value) => {
    // Only update local UI state here. The Apply button will dispatch changes to Redux and the physics system.
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section) => {
    setSectionsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const persistPhysicsPatch = (properties) => {
    if (!activeObject?.name) return;
    dispatch(
      setObjectPhysicsProperties({
        objectId: activeObject.name,
        properties,
      })
    );
  };

  const handleColliderTypeChange = (e) => {
    const nextType = e.target.value;
    handleChange('colliderType', nextType);
    persistPhysicsPatch({ colliderType: nextType });
  };

  const handleApplyPhysics = () => {
    const applyObjectId = activeObject?.name || currentObjectId;
    if (applyObjectId) {
      // Animation-Physics conflict check
      const isAnimated = animationObjects.some(
        (a) => a.id === applyObjectId || a.name === applyObjectId
      );
      if (isAnimated) {
        toast.error(
          "Physics and animation can't be used together. Remove the object from animation to continue.",
          { style: { background: '#1d1e21', color: '#fff' } }
        );
        return;
      }

      // Helper to handle numeric inputs, allowing 0 but falling back to default if invalid
      const parseNum = (val, def) => {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? def : parsed;
      };

      // Build a typed properties object from the local `data` state
      // Structure matches MongoDB schema
      const props = {
        mass: parseNum(data.mass, 5),
        linearDamping: parseNum(data.linearDamping, 0.1),
        angularDamping: parseNum(data.angularDamping, 0.1),
        automaticCenterOfMass: !!data.automaticCenterOfMass,
        automaticTensor: !!data.automaticTensor,
        inertiaTensor: {
          x: parseNum(data.inertiaTensorX, 1),
          y: parseNum(data.inertiaTensorY, 1),
          z: parseNum(data.inertiaTensorZ, 1),
        },
        inertiaTensorRotation: {
          x: parseNum(data.inertiaTensorRotationX, 0),
          y: parseNum(data.inertiaTensorRotationY, 0),
          z: parseNum(data.inertiaTensorRotationZ, 0),
        },
        useGravity: !!data.useGravity,
        isKinematic: !!data.isKinematic,
        interpolate: data.interpolate,
        collisionDetection: data.collisionDetection,
        freezePosition: {
          x: !!data.freezePositionX,
          y: !!data.freezePositionY,
          z: !!data.freezePositionZ,
        },
        freezeRotation: {
          x: !!data.freezeRotationX,
          y: !!data.freezeRotationY,
          z: !!data.freezeRotationZ,
        },
        colliderType: data.colliderType,
        isViewCollider: !!data.isViewCollider,
        // Physics material structure matches MongoDB schema: { name, friction, restitution, frictionEquation }
        physicsMaterial: {
          name: data.physicsMaterial || 'Default',
          friction: parseNum(data.friction, 0.5),
          restitution: parseNum(data.restitution, 0.7),
          frictionEquation: 'Default',
        },
      };

      // Add center of mass only when automaticCenterOfMass is false
      // Use centerOfMass to match backend schema (not centerOfMassOffset)
      if (!data.automaticCenterOfMass) {
        props.centerOfMass = {
          x: parseNum(data.centerOfMassX, 0),
          y: parseNum(data.centerOfMassY, 0),
          z: parseNum(data.centerOfMassZ, 0),
        };
      }

      // Dispatch the apply thunk which updates Redux state
      // This saves physics properties to Redux, which will be applied when entering preview mode
      dispatch(
        applyPhysicsThunk({
          objectId: applyObjectId,
          properties: props,
        })
      );

      // Only apply physics to the world if we're in preview mode
      // In design mode, we only save to Redux state
      if (isPreviewMode) {
        try {
          // Prefer using the provided scene to find the mesh
          let mesh = null;
          if (scene && typeof scene.getObjectByName === 'function') {
            mesh = scene.getObjectByName(applyObjectId);
          }

          // If not in scene, try using the selected object from redux
          if (
            !mesh &&
            activeObject &&
            activeObject.name === applyObjectId
          ) {
            mesh = activeObject;
          }

          if (mesh) {
            if (hasPhysicsUtil(applyObjectId)) {
              updateObjectPhysicsUtil(applyObjectId, props);
            } else {
              applyPhysicsUtil(applyObjectId, mesh, props);
            }
          } else {
            if (hasPhysicsUtil(applyObjectId)) {
              updateObjectPhysicsUtil(applyObjectId, props);
            }
          }
        } catch (e) {
          console.error(
            'PhysicsComponent: Failed to apply/update physics immediately',
            e
          );
        }
      }

      toast.success('Physics properties applied successfully!');
      dispatch(updateActiveComponent({ option: 'Physics', type: 'add' }));
    }
  };

  const handleRemovePhysics = () => {
    const removeObjectId = activeObject?.name || currentObjectId;
    if (!removeObjectId) {
      toast.error('No object selected');
      return;
    }

    if (!currentPhysicsData) {
      toast.error('This object has no physics applied');
      return;
    }

    dispatch(removePhysicsFromObject(removeObjectId));

    if (isPreviewMode && hasPhysicsUtil(removeObjectId)) {
      try {
        removePhysicsUtil(removeObjectId);
      } catch (e) {
        console.error('PhysicsComponent: Failed to remove preview physics body', e);
      }
    }

    setData(buildFormDataFromPhysics(defaultPhysicsProperties));
    dispatch(updateActiveComponent({ option: 'Physics', type: 'remove' }));
    toast.success('Physics removed from object');
  };

  return (
    <div className="text-gray-200 rounded-lg w-[220px]">
      {/* Header with back button and object info */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-600">
        <span className="text-xs text-gray-300">
          {activeObject?.name
            ? `Object: ${getObjectDisplayName(activeObject)}`
            : 'No Object Selected'}
        </span>
      </div>

      {/* Physics Controls */}
      <div className="space-y-0">
        {/* Apply/Remove Physics Buttons */}
        <div className="flex gap-1 mb-3">
          <button
            onClick={handleApplyPhysics}
            className="flex-1 bg-[#2B2C2F] text-white text-xs py-2 px-1 rounded"
          >
            Apply
          </button>
          <button
            onClick={handleRemovePhysics}
            disabled={!currentPhysicsData}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs py-2 px-1 rounded"
          >
            Remove
          </button>
        </div>
        <GridInput
          label="Mass"
          value={data.mass}
          onChange={(e) => handleChange('mass', e.target.value)}
        />
        <EnhancedSliderControl
          label="Linear Damping"
          value={Number(data.linearDamping) || 0}
          onChange={(val) => handleChange('linearDamping', val)}
          min={0}
          max={1}
          step={0.01}
        />
        <EnhancedSliderControl
          label="Angular Damping"
          value={Number(data.angularDamping) || 0}
          onChange={(val) => handleChange('angularDamping', val)}
          min={0}
          max={1}
          step={0.01}
        />
        <PhysicsRow label="Automatic Center">
          <PhysicsCheckbox
            checked={data.automaticCenterOfMass}
            onToggle={() =>
              handleChange('automaticCenterOfMass', !data.automaticCenterOfMass)
            }
          />
        </PhysicsRow>
        {!data.automaticCenterOfMass && (
          <Vector3Input
            label="Center Of Mass"
            x={data.centerOfMassX}
            y={data.centerOfMassY}
            z={data.centerOfMassZ}
            onChangeX={(e) => handleChange('centerOfMassX', e.target.value)}
            onChangeY={(e) => handleChange('centerOfMassY', e.target.value)}
            onChangeZ={(e) => handleChange('centerOfMassZ', e.target.value)}
          />
        )}
        <PhysicsRow label="Automatic Tensor">
          <PhysicsCheckbox
            checked={data.automaticTensor}
            onToggle={() =>
              handleChange('automaticTensor', !data.automaticTensor)
            }
          />
        </PhysicsRow>
        {!data.automaticTensor && (
          <>
            <Vector3Input
              label="Inertia Tensor"
              x={data.inertiaTensorX}
              y={data.inertiaTensorY}
              z={data.inertiaTensorZ}
              onChangeX={(e) => handleChange('inertiaTensorX', e.target.value)}
              onChangeY={(e) => handleChange('inertiaTensorY', e.target.value)}
              onChangeZ={(e) => handleChange('inertiaTensorZ', e.target.value)}
            />

            <Vector3Input
              label="Tensor Rotation"
              x={data.inertiaTensorRotationX}
              y={data.inertiaTensorRotationY}
              z={data.inertiaTensorRotationZ}
              onChangeX={(e) =>
                handleChange('inertiaTensorRotationX', e.target.value)
              }
              onChangeY={(e) =>
                handleChange('inertiaTensorRotationY', e.target.value)
              }
              onChangeZ={(e) =>
                handleChange('inertiaTensorRotationZ', e.target.value)
              }
            />
          </>
        )}
        <PhysicsRow label="Use Gravity">
          <PhysicsCheckbox
            checked={data.useGravity}
            onToggle={() => handleChange('useGravity', !data.useGravity)}
          />
        </PhysicsRow>
        <PhysicsRow label="Is Kinematic">
          <PhysicsCheckbox
            checked={data.isKinematic}
            onToggle={() => handleChange('isKinematic', !data.isKinematic)}
          />
        </PhysicsRow>
        <PhysicsRow label="Interpolate">
          <div className="w-full [&>div]:mb-0 [&>div]:w-full">
            <GridDropdown
              value={data.interpolate}
              onChange={(e) => handleChange('interpolate', e.target.value)}
              options={[
                { value: 'None', label: 'None' },
                { value: 'Interpolate', label: 'Interpolate' },
                { value: 'Extrapolate', label: 'Extrapolate' },
              ]}
            />
          </div>
        </PhysicsRow>
        <PhysicsRow label="Collision">
          <div className="w-full [&>div]:mb-0 [&>div]:w-full">
            <GridDropdown
              value={data.collisionDetection}
              menuClassName="homepage-scrollbar"
              onChange={(e) =>
                handleChange('collisionDetection', e.target.value)
              }
              options={[
                { value: 'Discrete', label: 'Discrete' },
                { value: 'Continuous', label: 'Continuous' },
                { value: 'ContinuousDynamic', label: 'Continuous Dynamic' },
                {
                  value: 'ContinuousSpeculative',
                  label: 'Continuous Speculative',
                },
              ]}
            />
          </div>
        </PhysicsRow>

        {/* <CollapsibleSection
          title="Colliders"
          isOpen={sectionsOpen.colliders}
          onToggle={() => toggleSection('colliders')}
        > */}
        <PhysicsRow label="Collider Type">
          <div className="w-full [&>div]:mb-0 [&>div]:w-full">
            <GridDropdown
              value={data.colliderType}
              onChange={handleColliderTypeChange}
              options={colliderTypes}
              menuClassName="homepage-scrollbar"
            />
          </div>
        </PhysicsRow>
        <PhysicsRow label="View Collider">
          <PhysicsCheckbox
            checked={!!data.isViewCollider}
            onToggle={() => {
              const next = !data.isViewCollider;
              handleChange('isViewCollider', next);
              persistPhysicsPatch({ isViewCollider: next });
            }}
          />
        </PhysicsRow>
        {/* </CollapsibleSection> */}

        <CollapsibleSection
          title="Constraints"
          isOpen={sectionsOpen.constraints}
          onToggle={() => toggleSection('constraints')}
        >
          <PhysicsRow label="Freeze Position">
            <div className="flex items-center gap-3.5">
              {['X', 'Y', 'Z'].map((axis) => (
                <div key={axis} className="flex items-center gap-0.5">
                  <PhysicsCheckbox
                    checked={data[`freezePosition${axis}`]}
                    onToggle={() =>
                      handleChange(
                        `freezePosition${axis}`,
                        !data[`freezePosition${axis}`]
                      )
                    }
                  />
                  <span className="text-gray-300 text-[10px]">{axis}</span>
                </div>
              ))}
            </div>
          </PhysicsRow>

          <PhysicsRow label="Freeze Rotation">
            <div className="flex items-center gap-3.5">
              {['X', 'Y', 'Z'].map((axis) => (
                <div key={axis} className="flex items-center gap-0.5">
                  <PhysicsCheckbox
                    checked={data[`freezeRotation${axis}`]}
                    onToggle={() =>
                      handleChange(
                        `freezeRotation${axis}`,
                        !data[`freezeRotation${axis}`]
                      )
                    }
                  />
                  <span className="text-gray-300 text-[10px]">{axis}</span>
                </div>
              ))}
            </div>
          </PhysicsRow>
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default PhysicsComponent;